import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { cx } from '@/lib/clases'
import { formatearRNC } from '@/lib/rnc'
import { InsigniaEstado } from '@/ui/InsigniaEstado'
import { LineaEsqueleto } from '@/ui/Esqueleto'
import { nombreAmbiente, presentacionEstadoTenant } from '@/dominio/consola/tipos'
import { useTenant } from '@/dominio/consola/useConsola'
import { ErrorDeCarga } from '../-comun'
import { PestanaResumen } from './-pestanas/Resumen'
import { PestanaPerfil } from './-pestanas/Perfil'
import { PestanaUsuarios } from './-pestanas/Usuarios'
import { PestanaClaves } from './-pestanas/Claves'
import { PestanaMotor } from './-pestanas/Motor'
import { PestanaCertificados } from './-pestanas/Certificados'
import { PestanaSecuencias } from './-pestanas/Secuencias'
import { PestanaColas } from './-pestanas/Colas'
import { PestanaContingencia } from './-pestanas/Contingencia'
import { PestanaPlan } from './-pestanas/Plan'
import { PestanaCobros } from './-pestanas/Cobros'
import { PestanaComplementos } from './-pestanas/Complementos'
import { PestanaAuditoria } from './-pestanas/Auditoria'
import { PestanaSoporte } from './-pestanas/Soporte'

/**
 * La ficha de un cliente en la consola (`docs/09` §6): una pestaña por cosa que
 * el dueño opera. Cobros es la cuenta del cliente con Agentical Group (`docs/adr/0020`).
 *
 * La pestaña va en la URL (`?pestana=certificados`) para que «mira los
 * certificados de este cliente» se pueda pasar como enlace, y para que volver
 * del modo soporte aterrice donde se salió.
 */

const PESTANAS = [
  { id: 'resumen', etiqueta: 'Resumen' },
  { id: 'perfil', etiqueta: 'Perfil' },
  { id: 'usuarios', etiqueta: 'Usuarios' },
  { id: 'claves', etiqueta: 'Claves de integración' },
  { id: 'motor', etiqueta: 'Motor y ambiente' },
  { id: 'certificados', etiqueta: 'Certificados' },
  { id: 'secuencias', etiqueta: 'Secuencias' },
  { id: 'colas', etiqueta: 'Colas' },
  { id: 'contingencia', etiqueta: 'Contingencia' },
  { id: 'plan', etiqueta: 'Plan' },
  { id: 'cobros', etiqueta: 'Cobros' },
  { id: 'complementos', etiqueta: 'Complementos' },
  { id: 'auditoria', etiqueta: 'Auditoría' },
  { id: 'soporte', etiqueta: 'Soporte' },
] as const

export type IdPestana = (typeof PESTANAS)[number]['id']

function esPestana(valor: unknown): valor is IdPestana {
  return PESTANAS.some((p) => p.id === valor)
}

export const Route = createFileRoute('/consola/clientes/$id')({
  validateSearch: (busqueda: Record<string, unknown>): { pestana?: IdPestana } => ({
    pestana: esPestana(busqueda.pestana) && busqueda.pestana !== 'resumen' ? busqueda.pestana : undefined,
  }),
  component: FichaCliente,
})

function FichaCliente() {
  const { id } = Route.useParams()
  const { pestana = 'resumen' } = Route.useSearch()
  const consulta = useTenant(id)
  const tenant = consulta.data

  return (
    <div className="flex flex-col gap-4">
      <Link
        to="/consola/clientes"
        className="inline-flex w-fit items-center gap-1 text-etiqueta text-gris-600 hover:text-gris-900"
      >
        <ArrowLeft aria-hidden="true" className="size-4" />
        Clientes
      </Link>

      <header className="flex flex-col gap-1">
        {tenant === undefined ? (
          consulta.isError ? null : (
            <div className="flex flex-col gap-2">
              <LineaEsqueleto ancho="w-1/3" />
              <LineaEsqueleto ancho="w-1/5" />
            </div>
          )
        ) : (
          <>
            <div className="flex flex-wrap items-baseline gap-3">
              <h1 className="text-pantalla text-gris-900">{tenant.razonSocial}</h1>
              <InsigniaEstado tono={presentacionEstadoTenant(tenant.estado).tono}>
                {presentacionEstadoTenant(tenant.estado).etiqueta}
              </InsigniaEstado>
            </div>
            <p className="flex flex-wrap gap-x-3 text-cuerpo text-gris-600">
              <span className="numero">RNC {formatearRNC(tenant.rnc)}</span>
              <span>Ambiente {nombreAmbiente(tenant.ambiente).toLowerCase()}</span>
              {tenant.nombreComercial ? <span>{tenant.nombreComercial}</span> : null}
            </p>
          </>
        )}
      </header>

      <nav aria-label="Secciones del cliente" className="border-b border-gris-300">
        <ul className="flex flex-wrap gap-1">
          {PESTANAS.map((p) => {
            const activa = p.id === pestana
            return (
              <li key={p.id}>
                <Link
                  to="/consola/clientes/$id"
                  params={{ id }}
                  search={{ pestana: p.id === 'resumen' ? undefined : p.id }}
                  aria-current={activa ? 'page' : undefined}
                  className={cx(
                    'inline-flex h-[var(--alto-control)] items-center border-b-2 px-3 text-cuerpo',
                    activa
                      ? 'border-marca font-medium text-marca'
                      : 'border-transparent text-gris-600 hover:text-gris-900',
                  )}
                >
                  {p.etiqueta}
                </Link>
              </li>
            )
          })}
        </ul>
      </nav>

      {consulta.isError ? (
        <ErrorDeCarga error={consulta.error} queCargaba="el cliente" onReintentar={() => void consulta.refetch()} />
      ) : tenant === undefined ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 5 }, (_, i) => (
            <LineaEsqueleto key={i} ancho={i % 2 === 0 ? 'w-2/3' : 'w-1/2'} />
          ))}
        </div>
      ) : (
        <section aria-label={PESTANAS.find((p) => p.id === pestana)?.etiqueta}>
          {pestana === 'resumen' ? <PestanaResumen tenant={tenant} /> : null}
          {pestana === 'perfil' ? <PestanaPerfil tenant={tenant} /> : null}
          {pestana === 'usuarios' ? <PestanaUsuarios tenant={tenant} /> : null}
          {pestana === 'claves' ? <PestanaClaves tenant={tenant} /> : null}
          {pestana === 'motor' ? <PestanaMotor tenant={tenant} /> : null}
          {pestana === 'certificados' ? <PestanaCertificados tenant={tenant} /> : null}
          {pestana === 'secuencias' ? <PestanaSecuencias tenant={tenant} /> : null}
          {pestana === 'colas' ? <PestanaColas tenant={tenant} /> : null}
          {pestana === 'contingencia' ? <PestanaContingencia tenant={tenant} /> : null}
          {pestana === 'plan' ? <PestanaPlan tenant={tenant} /> : null}
          {pestana === 'cobros' ? <PestanaCobros tenant={tenant} /> : null}
          {pestana === 'complementos' ? <PestanaComplementos tenant={tenant} /> : null}
          {pestana === 'auditoria' ? <PestanaAuditoria tenant={tenant} /> : null}
          {pestana === 'soporte' ? <PestanaSoporte tenant={tenant} /> : null}
        </section>
      )}
    </div>
  )
}
